import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuthBootstrap } from '@/shared/lib';
import { env } from '@/shared/lib/config/env';
import {
  AUTH_BLOCK_CODE_DEACTIVATED,
  getLocalStorageItem,
  removeLocalStorageItem,
  setAuthBlockState,
} from '@/shared/utils';

export type NotificationSubscription = {
  resource_type: 'meeting_group' | 'conversation' | 'support_ticket' | 'support_summary';
  resource_id: string;
};

export type NotificationMessage = {
  type: string;
  resource_type?: string;
  resource_id?: string;
  payload?: Record<string, unknown>;
};

type NotificationsWSContextValue = {
  isConnected: boolean;
  subscribe: (
    subscription: NotificationSubscription,
    onMessage: (msg: NotificationMessage) => void,
  ) => () => void;
};

type HandlerEntry = {
  subscription: NotificationSubscription;
  onMessage: (msg: NotificationMessage) => void;
};

const NotificationsWSContext = createContext<NotificationsWSContextValue | null>(null);

function buildWsUrl(token: string): string {
  const baseUrl = env.VITE_WS_URL_NOTIFICATIONS.replace(/^http/, 'ws');
  return `${baseUrl}?token=${encodeURIComponent(token)}`;
}

function send(ws: WebSocket, action: string, resource_type: string, resource_id: string): void {
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ action, resource_type, resource_id }));
  }
}

function topicKey(resource_type: string, resource_id: string): string {
  return `${resource_type}:${resource_id}`;
}

function matchesSubscription(
  subscription: NotificationSubscription,
  message: NotificationMessage,
): boolean {
  if (message.resource_type !== subscription.resource_type) {
    return false;
  }
  if (subscription.resource_type === 'support_summary' && subscription.resource_id === 'me') {
    return true;
  }
  return message.resource_id === subscription.resource_id;
}

export const NotificationsWSProvider = ({ children }: { children: ReactNode }) => {
  const { hasToken } = useAuthBootstrap();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef(new Map<string, HandlerEntry>());
  const subscribedTopicsRef = useRef(new Map<string, NotificationSubscription>());
  const sequenceRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);

  const syncSubscriptions = useCallback((ws: WebSocket) => {
    const desiredTopics = new Map<string, NotificationSubscription>();
    handlersRef.current.forEach(({ subscription }) => {
      desiredTopics.set(
        topicKey(subscription.resource_type, subscription.resource_id),
        subscription,
      );
    });

    subscribedTopicsRef.current.forEach((subscription, key) => {
      if (!desiredTopics.has(key)) {
        send(ws, 'unsubscribe', subscription.resource_type, subscription.resource_id);
        subscribedTopicsRef.current.delete(key);
      }
    });

    desiredTopics.forEach((subscription, key) => {
      if (!subscribedTopicsRef.current.has(key)) {
        send(ws, 'subscribe', subscription.resource_type, subscription.resource_id);
        subscribedTopicsRef.current.set(key, subscription);
      }
    });
  }, []);

  const ensureSocket = useCallback(() => {
    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.CONNECTING || existing.readyState === WebSocket.OPEN)
    ) {
      return existing;
    }

    if (!hasToken) {
      return null;
    }

    const token = getLocalStorageItem('token');
    if (!token) {
      return null;
    }

    if (existing) {
      // New socket should rebuild subscriptions from current handlers.
      subscribedTopicsRef.current.clear();
      setIsConnected(false);
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const newWs = new WebSocket(buildWsUrl(token));
    wsRef.current = newWs;

    newWs.onopen = () => {
      if (wsRef.current !== newWs) {
        return;
      }
      reconnectAttemptRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(true);
      syncSubscriptions(newWs);
    };

    newWs.onclose = (event) => {
      if (wsRef.current === newWs) {
        setIsConnected(false);
        wsRef.current = null;
        subscribedTopicsRef.current.clear();

        if (
          event.reason !== AUTH_BLOCK_CODE_DEACTIVATED &&
          handlersRef.current.size > 0 &&
          !reconnectTimeoutRef.current
        ) {
          const delayMs = Math.min(500 * 2 ** reconnectAttemptRef.current, 5000);
          reconnectAttemptRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            const reconnected = ensureSocket();
            if (reconnected?.readyState === WebSocket.OPEN) {
              syncSubscriptions(reconnected);
            }
          }, delayMs);
        }
      }

      if (event.reason === AUTH_BLOCK_CODE_DEACTIVATED) {
        removeLocalStorageItem('token');
        setAuthBlockState({
          code: AUTH_BLOCK_CODE_DEACTIVATED,
          message: 'Ваш аккаунт деактивирован',
        });
      }
    };

    newWs.onerror = () => {
      newWs.close();
    };

    newWs.onmessage = (event) => {
      if (wsRef.current !== newWs) {
        return;
      }
      try {
        const data = JSON.parse(event.data) as NotificationMessage;
        if (data.type === 'subscribed' || data.type === 'unsubscribed') {
          return;
        }
        handlersRef.current.forEach(({ subscription, onMessage }) => {
          if (matchesSubscription(subscription, data)) {
            onMessage(data);
          }
        });
      } catch {
        // ignore non-JSON or invalid payloads
      }
    };

    return newWs;
  }, [hasToken, syncSubscriptions]);

  const subscribe = useCallback(
    (subscription: NotificationSubscription, onMessage: (msg: NotificationMessage) => void) => {
      const handlerId = `subscription-${sequenceRef.current++}`;
      handlersRef.current.set(handlerId, { subscription, onMessage });

      const ws = ensureSocket();
      if (ws?.readyState === WebSocket.OPEN) {
        syncSubscriptions(ws);
      }

      return () => {
        handlersRef.current.delete(handlerId);
        if (handlersRef.current.size === 0) {
          reconnectAttemptRef.current = 0;
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        }

        const currentWs = wsRef.current;
        if (!currentWs) {
          return;
        }
        if (currentWs.readyState === WebSocket.OPEN) {
          syncSubscriptions(currentWs);
        }
        if (handlersRef.current.size === 0) {
          currentWs.close();
          wsRef.current = null;
          subscribedTopicsRef.current.clear();
          setIsConnected(false);
        }
      };
    },
    [ensureSocket, syncSubscriptions],
  );

  useEffect(() => {
    return () => {
      const ws = wsRef.current;
      if (ws) {
        subscribedTopicsRef.current.forEach((subscription) => {
          send(ws, 'unsubscribe', subscription.resource_type, subscription.resource_id);
        });
        ws.close();
      }
      reconnectAttemptRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      wsRef.current = null;
      handlersRef.current.clear();
      subscribedTopicsRef.current.clear();
      setIsConnected(false);
    };
  }, []);

  return (
    <NotificationsWSContext.Provider value={{ isConnected, subscribe }}>
      {children}
    </NotificationsWSContext.Provider>
  );
};

export function useNotificationsWSContext(): NotificationsWSContextValue {
  const ctx = useContext(NotificationsWSContext);
  if (!ctx) {
    throw new Error('useNotificationsWS must be used within NotificationsWSProvider');
  }
  return ctx;
}
